import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PhoneCall, Flame, AlertTriangle, CalendarCheck } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Avatar, Staff } from "../components/StaffAvatar";
import { Birthdays } from "../components/Birthdays";
import { funLine } from "../components/LeadNudger";
import { Toppers } from "../components/Toppers";
import { WhatsNew, UidUpdates } from "../components/HomeUpdates";
import { WeeklyStar } from "../components/WeeklyStar";

const hourIST = () => Number(new Date().toLocaleString("en-US", { timeZone: "Asia/Kolkata", hour: "numeric", hour12: false })) % 24;
const greet = (h) => (h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening");
const time = (iso) => new Date(iso).toLocaleTimeString("en-IN", { timeZone: "Asia/Kolkata", hour: "numeric", minute: "2-digit" });
const ago = (iso) => { const m = Math.max(0, Math.round((Date.now() - new Date(iso)) / 60000)); return m < 60 ? `${m} min ago` : `${Math.round(m / 60)} h ago`; };

const Card = ({ icon: Icon, title, count, tint, link, tid, children }) => (
  <div className="rounded-2xl border p-5" style={{ background: tint }} data-testid={tid}>
    <div className="flex items-center gap-2 mb-3"><Icon className="h-4 w-4 text-primary" /><span className="font-semibold">{title}</span>
      <span className="num text-sm text-muted-foreground" data-testid={`${tid}-count`}>{count}</span>
      {link && <Link to={link} className="ml-auto text-xs text-primary hover:underline" data-testid={`${tid}-all`}>See all</Link>}</div>
    {children}
  </div>
);
const Row = ({ to, tid, children }) => <Link to={to} data-testid={tid} className="flex flex-wrap items-center gap-x-3 gap-y-0.5 py-2 border-t border-black/5 text-sm hover:text-primary">{children}</Link>;
const None = ({ text }) => <p className="text-sm text-muted-foreground">{text}</p>;
const Lead = ({ l }) => <span className="font-medium truncate flex-1 min-w-0">{l.company_name || l.contact_name}</span>;

const useHomeData = (user, can, mine) => {
  const [cb, setCb] = useState(null);
  const [hot, setHot] = useState(null);
  const [risk, setRisk] = useState(null);
  const [fu, setFu] = useState(null);
  const [tasks, setTasks] = useState([]);
  const sales = can("sales", "read");
  const orders = can("orders", "read");
  const owner = user?.staff_name;
  useEffect(() => {
    api.get("/sales/callbacks").then((r) => setCb(r.data.items)).catch(() => setCb([]));
    api.get("/sales/hot-leads").then((r) => setHot(r.data)).catch(() => setHot([]));
    if (sales) {
      api.get("/leads", { params: { due: "today", page_size: 6, ...(owner ? { owner } : {}) } }).then((r) => setFu(r.data)).catch(() => setFu({ items: [], total: 0 }));
      api.get("/followup-tasks/mine").then((r) => setTasks(r.data)).catch(() => {});
    }
    if (orders) api.get("/orders", { params: { risk: "late", page_size: 6, ...(mine ? { owner } : {}) } }).then((r) => setRisk(r.data)).catch(() => setRisk({ items: [], total: 0 }));
  }, [sales, orders, owner, mine]);
  return { cb, hot, risk, fu, tasks };
};

const FollowupCard = ({ fu, tasks, fun }) => (
  <Card icon={CalendarCheck} title="Follow up today" count={fu ? fu.total + tasks.length : ""} tint="#eef4ff" link="/sales/followups" tid="home-followups">
    <p className="text-xs italic text-muted-foreground mb-2" data-testid="home-followups-fun">{fun}</p>
    {tasks.slice(0, 3).map((t) => (
      <Row key={t.task_id} to={`/sales/leads/${t.lead_id}`} tid={`home-task-${t.task_id}`}>
        <span className="text-xs px-2 rounded-full bg-amber-100 text-amber-700">Assigned</span>
        <span className="font-medium truncate flex-1 min-w-0">{t.company_name || t.contact_name}</span><span className="text-xs text-muted-foreground">{t.due_date}</span></Row>))}
    {fu && !fu.items.length && !tasks.length && <None text="Nothing due today. Maybe plan tomorrow's calls?" />}
    {fu && fu.items.map((l) => (
      <Row key={l.lead_id} to={`/sales/leads/${l.lead_id}`} tid={`home-fu-${l.lead_id}`}>
        <Lead l={l} /><span className="text-xs text-muted-foreground truncate max-w-[45%]">{l.next_action || ""}</span></Row>))}
  </Card>
);

const CallbackCard = ({ cb }) => (
  <Card icon={PhoneCall} title="Your callbacks" count={cb?.length ?? ""} tint="#eefaf4" link="/sales/callbacks" tid="home-callbacks">
    {cb && !cb.length && <None text="No callbacks waiting. Nice work." />}
    {cb && cb.slice(0, 6).map((l) => (
      <Row key={l.lead_id} to={`/sales/leads/${l.lead_id}`} tid={`home-cb-${l.lead_id}`}>
        <span className={`text-xs px-2 rounded-full ${l.is_due ? "bg-primary/15 text-primary" : "bg-black/5 text-muted-foreground"}`}>{l.is_due ? "Due" : time(l.due_at)}</span>
        <Lead l={l} /></Row>))}
  </Card>
);

const HotCard = ({ hot }) => (
  <Card icon={Flame} title="Hot leads" count={hot?.length ?? ""} tint="#fff4ec" link="/sales/dashboard" tid="home-hot">
    {hot && !hot.length && <None text="No one opened a shared file in the last 48 hours." />}
    {hot && hot.slice(0, 6).map((l) => (
      <Row key={l.lead_id} to={`/sales/leads/${l.lead_id}`} tid={`home-hot-${l.lead_id}`}>
        <Lead l={l} /><span className="text-xs text-muted-foreground">{ago(l.last_file_open.at)}</span></Row>))}
  </Card>
);

const RiskCard = ({ risk, mine }) => (
  <Card icon={AlertTriangle} title={mine ? "Your UIDs at risk" : "UIDs at risk"} count={risk?.total ?? ""} tint="#f4f1ff" link="/orders/list" tid="home-risk">
    {risk && !risk.items.length && <None text="No UIDs past their delivery date." />}
    {risk && risk.items.map((o) => (
      <Row key={o.uid} to={`/orders/uid/${o.uid}`} tid={`home-risk-${o.uid}`}>
        <span className="font-mono text-xs text-primary">{o.uid}</span><span className="font-medium truncate flex-1 min-w-0">{o.client_name}</span>
        <span className="text-xs" style={{ color: "#c8344f" }}>due {o.commit_date}</span><span className="w-full text-xs text-muted-foreground"><Staff name={o.owner} size={16} /></span></Row>))}
  </Card>
);

export default function Home() {
  const { user, can } = useAuth();
  const [fun] = useState(funLine);
  const mine = !can("orders", "assign") && user?.staff_name;
  const { cb, hot, risk, fu, tasks } = useHomeData(user, can, mine);
  const name = user?.staff_name || (user?.name || "").split(" ")[0] || "there";
  const today = new Date().toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata", weekday: "long", day: "numeric", month: "long" });
  return (
    <div className="max-w-6xl">
      <div className="flex items-center gap-4 mb-8 ax-fade">
        <Avatar name={user?.staff_name} size={52} />
        <div><h1 className="text-3xl sm:text-4xl font-bold tracking-tight" data-testid="home-greeting">{greet(hourIST())}, {name}</h1>
          <p className="text-muted-foreground mt-1">{today}. Here's what needs you today.</p></div>
      </div>
      <WhatsNew />
      <Birthdays />
      <Toppers />
      <WeeklyStar />
      <UidUpdates />
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        {can("sales", "read") && <FollowupCard fu={fu} tasks={tasks} fun={fun} />}
        <CallbackCard cb={cb} />
        <HotCard hot={hot} />
        {can("orders", "read") && <RiskCard risk={risk} mine={mine} />}
      </div>
    </div>
  );
}
